import { useRef } from "react";

import { Feature, FeatureCollection, Geometry, Position } from "geojson";

import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset";
import { Dataset } from "@/types/dataset";
import { reorderLayerBasedOnArea } from "@/utils/map";
import { wktToGeoJSON } from "@/utils/wkt";

type ProfilingMarkerItem = {
  name?: string;
  coordinates: [number, number] | Position;
};

type UseSiteProfilingLayersProps = {
  handleAddPolygonLayer: (
    dataset: Dataset,
    data: FeatureCollection<Geometry>,
    customId?: string
  ) => void;
  handleAddMarkers: (
    data: { name?: string; coordinates: [number, number] | Position }[],
    draggable: boolean
  ) => maplibregl.Marker[];
  hideAllMarkers: () => void;
};

const LOCATION_NAME_KEY = "Location Name";
const LOCATION_ID_KEY = "Location ID";
const LOCATION_GEOMETRY_KEY = "Location Geometry";

/**
 * Resolves the location point of a Site Profiling row. The point lives in the
 * `Location Geometry` column (a WKT string, e.g. `POINT(106.8 -6.2)`) — it is
 * NOT emitted as a separate Point feature — so read it from the properties.
 * Falls back to a point-typed geometry / snake_case key for other data shapes.
 */
const resolveLocationCoordinates = (
  feature: Feature<Geometry>
): Position | undefined => {
  const raw =
    feature.properties?.[LOCATION_GEOMETRY_KEY] ??
    feature.properties?.location_geometry;

  if (typeof raw === "string") {
    const geometry = wktToGeoJSON(raw)?.geometry;
    return geometry?.type === "Point" ? geometry.coordinates : undefined;
  }
  if (raw && typeof raw === "object" && Array.isArray(raw.coordinates)) {
    return raw.coordinates;
  }
  if (feature.geometry?.type === "Point") {
    return feature.geometry.coordinates;
  }
  return undefined;
};

export default function useSiteProfilingLayers({
  handleAddPolygonLayer,
  handleAddMarkers,
  hideAllMarkers
}: UseSiteProfilingLayersProps) {
  // The profiling markers we last rendered per dataset, keyed by dataset id.
  // handleAddSiteProfiling is re-invoked on every layer-processing pass, so we
  // reconcile against this record: skip when the same markers are already on the
  // map (avoids stacking/churn, mirroring the polygon layer's getSource guard),
  // but rebuild when the underlying data changed — i.e. a re-run for the same
  // dataset id. A write-once Set couldn't detect the re-run and left it without
  // markers. handleAddMarkers also replaces its ref without removing the previous
  // DOM markers, so we remove the stale ones ourselves before re-adding.
  const renderedMarkersRef = useRef<
    Map<string, { markers: maplibregl.Marker[]; renderedMarkersData: string }>
  >(new Map());

  const handleAddSiteProfiling = (
    newDataset: Dataset,
    featureCollection: FeatureCollection<Geometry>
  ) => {
    const polygonFeatures: FeatureCollection<Geometry> = {
      type: "FeatureCollection",
      features: featureCollection.features.filter(
        (feature) =>
          feature.geometry?.type === "Polygon" ||
          feature.geometry?.type === "MultiPolygon"
      )
    };

    const sortedPolygonFeatures = reorderLayerBasedOnArea(polygonFeatures);

    // If we have polygon features, add a polygon layer
    if (sortedPolygonFeatures.features.length > 0) {
      const polygonDataset = {
        ...newDataset,
        data: {
          ...newDataset.data
        }
      };
      handleAddPolygonLayer(
        polygonDataset,
        sortedPolygonFeatures,
        `${polygonDataset.data.id}-polygon`
      );
    }

    // Markers are only shown for the spatial analysis result dataset.
    const isAnalysisResult =
      newDataset.data.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME;
    if (!isAnalysisResult) return;

    // One marker per location, taken from each row's `Location Geometry` point.
    // De-duplicated by location id so repeated rows for a location don't stack.
    const markersByLocationId = new Map<string, ProfilingMarkerItem>();

    featureCollection.features.forEach((feature, index) => {
      const locationId = String(
        feature.properties?.[LOCATION_ID_KEY] ??
          feature.properties?.location_id ??
          `feature-${index}`
      );
      if (markersByLocationId.has(locationId)) return;

      const coordinates = resolveLocationCoordinates(feature);
      if (!coordinates) return;

      markersByLocationId.set(locationId, {
        name: feature.properties?.[LOCATION_NAME_KEY],
        coordinates
      });
    });

    const items = Array.from(markersByLocationId.values());
    if (items.length === 0) return;

    // Skip when the markers we already rendered for this dataset match the
    // current data (a plain re-render), otherwise this would restack them.
    const renderedMarkersData = JSON.stringify(items);
    const previous = renderedMarkersRef.current.get(newDataset.data.id);
    if (previous?.renderedMarkersData === renderedMarkersData) return;

    // The data changed (a re-run): drop the stale markers we added last time so
    // they don't linger alongside the refreshed ones.
    previous?.markers.forEach((marker) => marker.remove());

    const markers = handleAddMarkers(items, false);
    renderedMarkersRef.current.set(newDataset.data.id, {
      markers: markers ?? [],
      renderedMarkersData
    });
    if (!newDataset.properties.isVisible) hideAllMarkers();
  };

  return { handleAddSiteProfiling };
}
